import { CategoryDataType } from "@/api/CategoryApi";
import { ProductDataType } from "@/api/ProductsApi";
import { importProducts } from "@/api/ProductImportApi";
import { PRODUCT_TYPES } from "@utils/Constants";
import {
    PRODUCT_IMPORT_FIELDS,
    REQUIRED_PRODUCT_IMPORT_FIELDS,
    findProductImportField,
} from "../productImportFields";
import { ProductColumnMapping } from "../productImportMapping";
import { buildProductImportPayload } from "../productImportPayload";
import {
    createMissingCategories,
    indexCategoriesByName,
    loadExistingProductData,
} from "../loadExistingProductData";
import { ProductResolveResult, applyCreatedCategories, resolveProductRows } from "../resolveProductRows";
import { suggestProductColumnMapping } from "../suggestProductColumnMapping";
import { ImportDefinition } from "../core/types";

export type ProductImportContext = {
    products: ProductDataType[];
    categories: CategoryDataType[];
};

/** Todo lo que el asistente de importacion necesita saber del modelo `Product`. */
export const productImportDefinition: ImportDefinition<ProductDataType, ProductImportContext> = {
    title: "Importar Productos",
    backUrl: "/user/products",
    doneActionTitle: "Ir a la lista de productos",
    entityPlural: "productos",
    fileHelpText:
        "Seleccione el archivo de productos exportado de su sistema anterior. Se aceptan archivos CSV y Excel, con o sin fila de encabezados.",
    mappingHelpText:
        "Indique a que dato del producto corresponde cada columna. Lo que no asigne toma su valor por defecto: unidad de medida Unidad, impuesto IVA al 13%, minimo y maximo en 0, y el tipo (mercancia o servicio) se deduce del codigo CAByS.",
    duplicateQuestion: "Si el producto ya existe (por codigo o codigo de barras):",
    errorRowsHelp: (errorCount, totalRows) =>
        `${errorCount} de ${totalRows} filas no se pueden importar: les falta el codigo CAByS o el nombre del producto, y su categoria tampoco los aporta. Revise que las columnas correctas esten asignadas a "Codigo Cabys", "Descripcion de Factura (nombre del producto)" y "Categoria (por nombre)".`,
    doneHelpText:
        "El listado de productos se alimenta del buscador, que se actualiza unos segundos despues de la carga.",

    fields: PRODUCT_IMPORT_FIELDS,
    requiredFields: REQUIRED_PRODUCT_IMPORT_FIELDS,
    findField: findProductImportField,
    suggestMapping: suggestProductColumnMapping,

    loadContext: loadExistingProductData,

    resolveRows: ({ dataRows, mapping, context, policy }) =>
        resolveProductRows({
            dataRows,
            mapping: mapping as ProductColumnMapping,
            existingProducts: context.products,
            existingCategories: context.categories,
            policy,
        }),

    missingLookups: (result) => {
        const missing = (result as ProductResolveResult).missingCategories;
        if (!missing?.length) return null;
        return {
            label: `Se crearan ${missing.length} categorias nuevas`,
            names: missing.map((category) => category.nombre),
        };
    },

    previewColumns: [
        { header: "Codigo", render: (row) => row.data.codigo },
        { header: "Nombre", render: (row) => row.data.descripcion_fact },
        { header: "CAByS", render: (row) => row.data.codigo_cabys },
        {
            header: "Tipo",
            render: (row) => (PRODUCT_TYPES as Record<string, string>)[`${row.data.tipo_producto}`],
        },
        { header: "Existencias", render: (row) => row.data.existencias },
        { header: "Costo", render: (row) => row.data.costo },
    ],

    commit: async ({ resolved, context, onProgress }) => {
        const { missingCategories, providedFields } = resolved as ProductResolveResult;

        // Las categorias nuevas se crean antes para poder mandar `categoryId` ya resueltos.
        const categories = await createMissingCategories(missingCategories || [], context.categories);
        context.categories = categories;

        const rows = applyCreatedCategories(resolved.rows, indexCategoriesByName(categories));
        const payload = buildProductImportPayload({
            rows,
            providedFields,
            existingProducts: context.products,
        });
        const skippedCount = payload.filter((row) => row.action === "skip").length;

        const summary = await importProducts(payload, onProgress);

        return { summary, skippedCount };
    },
};

export default productImportDefinition;
